import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { TokenExchangeMethodSchema } from "../../../../schemas/AgentExternalTool/auth/CustomManifest/OAuth.schema";
import { ModelConfigBaseShape } from "../../../../schemas/ModelConfig/model-config.schema";
import { AiTaskModelNoticeSchema } from "../../../../schemas/ModelConfig/model-notice.schema";
import { AiTaskModelOverrideSchema } from "../../../../schemas/ModelConfig/model-override.schema";
import { RetiredEffortLevelFieldsShape } from "../../../../schemas/ModelConfig/retired-effort-level-fields";
import { RerankerModelSchema } from "../../../../shared/domain/documents/reranker-models";
import {
  optionalSafeExternalUrlSchema,
  safeExternalUrlSchema
} from "../../../../shared/network/safe-external-url.schema";
import {
  CustomModelIdSchema,
  FolderRefSchema,
  JsonSchemaDocumentSchema,
  PaginationParamsSchema,
  StoredCustomModelIdSchema
} from "./common.schemas";
import { DocumentMetadataFilterSchema } from "./documents.schemas";

// ============================================================================
// SHARED QUERY PARAMS
// ============================================================================

export const ListSkillsCommonParamsSchema = z.object({
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  // Filter to skills in a folder, by folder id or name (case-insensitive).
  // Applies to list endpoints that support folders (workflows, AI tasks).
  folder: z.string().optional()
});

// ============================================================================
// PATH PARAMS
// ============================================================================

// Every entity a skill route addresses has a `@db.Uuid` primary key — `Workflow.id`,
// `AITask.id`, `Collection.id`, `DocumentTemplate.id`. Feeding one of these columns a
// non-uuid can only ever fail as a Postgres cast error, so checking it here turns a
// 500 into a 400 that names the offending param.

export const WorkflowIdParamSchema = z.object({
  workflowId: z.string().uuid()
});

export const TaskIdParamSchema = z.object({
  taskId: z.string().uuid()
});

export const CollectionIdParamSchema = z.object({
  collectionId: z.string().uuid()
});

export const DocumentTemplateIdParamSchema = z.object({
  templateId: z.string().uuid()
});

// ============================================================================
// WORKFLOW
// ============================================================================

export const WorkflowSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  triggerType: z.string(),
  iconUrl: z.string().nullable(),
  agentInputSchema: z
    .unknown()
    .nullable()
    .describe(
      "The parameters this workflow accepts as a skill — copy it into `agentInputSchema` on `POST /agents/:agentId/tools`. On a PUBLISHED workflow it is the LIVE contract, read off the published graph a WORKFLOW skill is invoked against, so declaring what it reports is always accepted; on a DRAFT it is the draft trigger. Null when the workflow accepts no agent input."
    ),
  folder: FolderRefSchema,
  createdAt: z.string(),
  updatedAt: z.string()
});

export const ListWorkflowsResponseSchema = z.object({
  items: z.array(WorkflowSummarySchema),
  total: z.number()
});

// ============================================================================
// AI TASK
// ============================================================================

/**
 * What answers an AI task: GENERATIVE (a model that writes) or DECISION (a
 * decision model that answers typed questions). Read off the generated enum
 * values, so a kind added to the database schema reaches the contract unedited.
 */
const AITaskKindSchema = z.enum(DbEnum.A_I_TASK_KIND_VALUES);

export const TaskSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string(),
  kind: AITaskKindSchema.describe(
    "What answers the task: GENERATIVE (a model that writes) or DECISION (a decision model that answers typed questions). Set at create, never changed."
  ),
  inputFormat: z.string(),
  outputFormat: z.string(),
  modelProvider: z.string(),
  modelName: z.string(),
  folder: FolderRefSchema,
  createdAt: z.string(),
  updatedAt: z.string()
});

/**
 * The model tuning an AI task stores, as v1 publishes it.
 *
 * `AITask.modelConfig` is a `ModelConfigSchema` object. `TaskSummarySchema`
 * lifts two of its keys (`modelProvider`, `modelName`) and the detail schema
 * used to stop there, so six fields a caller can SET on create or update were
 * readable through no endpoint. `temperature` is the sharpest of them:
 * `CreateTaskBodySchema` declares `.default(0.7)`, so every create persists one
 * whether or not the caller sent it.
 *
 * Taken from `ModelConfigBaseShape` field by field rather than restated. A
 * hand-written copy of these enums is already live on the agents surface and
 * had already drifted from the canonical shape, which is exactly the failure this
 * derivation removes rather than repeats.
 *
 * `customModelId` is here rather than taken from `ModelConfigBaseShape`, which
 * does not carry it: the canonical shape scopes BYOM to `ModelConfigSchema`.
 *
 * 🚨 **This shape describes a RESPONSE, so its `customModelId` is the TOLERANT
 * `StoredCustomModelIdSchema` and NOT the uuid-bounded `CustomModelIdSchema`
 * the request bodies use.** The two are not interchangeable in either
 * direction, and the failure this one avoids is silent: `SkillsMapper` reads
 * every field in this shape through `.catch(undefined)`, so a stored value a
 * uuid bound refused would simply be OMITTED — a task running a customer's
 * endpoint would report running none. `id-validation-consistency.test.ts`
 * enforces the same split from the other side, holding only REQUEST schemas to
 * its cross-file rule.
 *
 * Every field is optional because a stored config need not carry it, and
 * because a provider-specific knob is meaningless on the other providers.
 */
export const TaskModelTuningShape = {
  temperature: ModelConfigBaseShape.temperature,
  reasoningLevel: ModelConfigBaseShape.reasoningLevel,
  thinkingDisplay: ModelConfigBaseShape.thinkingDisplay,
  customModelId: StoredCustomModelIdSchema.optional()
} as const;

export const TaskModelTuningSchema = z.object(TaskModelTuningShape);

export type TaskModelTuning = z.infer<typeof TaskModelTuningSchema>;

/**
 * One few-shot demonstration as v1 publishes it — a stored `FewShot` row.
 *
 * These are NOT part of the prompt. The executor turns each pair into a
 * user/assistant message pair ahead of the real input, so demonstrations stay
 * structured data rather than text a caller has to concatenate into `prompt`
 * (NEX-2592).
 */
export const TaskFewShotSchema = z.object({
  id: z.string(),
  input: z.string(),
  output: z.string()
});

export type TaskFewShot = z.infer<typeof TaskFewShotSchema>;

/**
 * One few-shot demonstration as a WRITE accepts it. No `id`: the set is
 * addressed as a whole on this surface — a create stores the array, an update
 * replaces it — so a caller never has to hold ids to edit one pair.
 *
 * Both halves are required and non-empty. An empty `input` or `output` is a
 * demonstration that teaches nothing and costs tokens on every run, and the
 * failure of storing one is silent: it lands in the message list as a blank
 * turn the model reads as an instruction to answer with nothing.
 */
export const TaskFewShotInputSchema = z.object({
  input: z.string().min(1, "input is required"),
  output: z.string().min(1, "output is required")
});

export type TaskFewShotInput = z.infer<typeof TaskFewShotInputSchema>;

export const TaskDetailSchema = TaskSummarySchema.extend({
  prompt: z.string().nullable(),
  jsonInputSchema: z.unknown().nullable(),
  jsonOutputSchema: z.unknown().nullable(),
  multimodal: z.boolean(),
  documentTemplateId: z.string().nullable(),
  /** Ordered oldest-first — the order the model is shown them in. */
  fewShots: z.array(TaskFewShotSchema),
  ...TaskModelTuningShape
});

/**
 * A task update also returns the version snapshot taken alongside the edit, so
 * the caller can surface or roll back to it. Both fields are null for a no-op
 * PATCH, which deliberately creates no version.
 *
 * `TaskDetailSchema` describes the update payload only as far as the task
 * itself — parsing an update response with it strips the snapshot silently,
 * because Zod drops unknown keys rather than complaining about them.
 */
export const UpdateTaskResponseSchema = TaskDetailSchema.extend({
  versionId: z.string().nullable(),
  versionCreatedAt: z.string().nullable()
});

export const ListTasksResponseSchema = z.object({
  items: z.array(TaskSummarySchema),
  total: z.number()
});

// ============================================================================
// COLLECTION
// ============================================================================

export const CollectionSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullable(),
  description: z.string().nullable(),
  documentCount: z.number(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string()
});

export const CollectionDetailSchema = CollectionSummarySchema.extend({
  k: z.number(),
  reranker: z.string().nullable(),
  preciseResponses: z.boolean(),
  includeMetadata: z.boolean()
});

export const ListCollectionsResponseSchema = z.object({
  items: z.array(CollectionSummarySchema),
  total: z.number()
});

// ============================================================================
// DOCUMENT TEMPLATE
// ============================================================================

export const DocumentTemplateSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  type: z.string(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
});

export const DocumentTemplateDetailSchema = DocumentTemplateSummarySchema.extend({
  inputFormat: z.unknown().nullable(),
  slidesInputFormat: z.array(z.unknown()),
  fileUrl: z.string().nullable(),
  previewFileUrl: z.string().nullable()
});

export const ListDocumentTemplatesResponseSchema = z.object({
  items: z.array(DocumentTemplateSummarySchema),
  total: z.number()
});

// ============================================================================
// CREATE BODY SCHEMAS
// ============================================================================

export const CreateDocumentTemplateBodySchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  description: z.string().optional(),
  type: z.enum([
    "WORD_FORMAT",
    "WORD_TEMPLATE",
    "WORD_CONTENT",
    "POWERPOINT_TEMPLATE",
    "EXCEL_TEMPLATE"
  ])
});

type RefusedField = z.ZodOptional<z.ZodUnknown>;

/**
 * A family of field names declared only to be refused.
 *
 * A v1 write body is a plain `z.object()`, which strips what it does not
 * declare and answers 200 — so a key the caller guessed, or one that belongs to
 * another surface, reported success for a write that never happened. Declaring
 * the name lets the schema answer an actionable 400 at `path: [name]` instead.
 *
 * Every box is `z.unknown()` rather than the shape the caller probably meant,
 * so that ANY value under the key trips the refinement: typed as an array,
 * `examples: "a, b"` would be a shape error about arrays instead of a message
 * naming the right field; typed as a string, `promptText: 123` would be a type
 * error instead of the alias message. The message names the field, not the
 * value, on purpose.
 *
 * The `.describe()` is not decoration. `z.unknown()` converts to a bare `{}`,
 * and `contract-input-schema-fidelity.spec.ts` refuses a NAMED field an MCP
 * caller is offered with nothing said about it — correctly, since `{}` reads as
 * "send anything" where the truth is "do not send this at all".
 */
function refusedFields<Name extends string>(
  names: readonly Name[],
  text: {
    describeFor: (name: Name) => string;
    messageFor: (name: Name) => string;
  }
): {
  fields: Record<Name, RefusedField>;
  refine: (data: Partial<Record<Name, unknown>>, ctx: z.RefinementCtx) => void;
} {
  const entries = names.map((name): [Name, RefusedField] => [
    name,
    z.unknown().optional().describe(text.describeFor(name))
  ]);
  // `Object.fromEntries` widens the key to `string`; every key is one of
  // `names` and every name is present, so the record is total over `Name`.
  const fields = Object.fromEntries(entries) as Record<Name, RefusedField>;
  return {
    fields,
    refine(data, ctx) {
      for (const name of names) {
        if (data[name] !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [name],
            message: text.messageFor(name)
          });
        }
      }
    }
  };
}

// Field names callers commonly (and mistakenly) reach for when trying to set
// the task prompt. None of these are persisted; before NEX-2042 they were
// silently stripped, so the task was created/updated successfully but left
// unusable ("Prompt is required" only surfaced at execute-time).
const PROMPT_FIELD_ALIASES = ["promptText", "systemPrompt", "instructions", "text"] as const;

const promptAliases = refusedFields(PROMPT_FIELD_ALIASES, {
  describeFor: (name) =>
    `Not a field. The task prompt goes in "prompt" (top level) or "generation.prompt"; "${name}" is rejected with a 400.`,
  messageFor: (name) =>
    `Unsupported field "${name}". Set the task prompt via "prompt" (top level) or "generation.prompt".`
});

// The names a caller reaches for when attaching few-shot examples, none of
// which is the field: a create carrying `examples` reported success and stored
// nothing, and the caller only found out by reading the task back (NEX-2592).
const FEW_SHOT_FIELD_ALIASES = [
  "examples",
  "fewShotExamples",
  "samples",
  "demonstrations"
] as const;

const fewShotAliases = refusedFields(FEW_SHOT_FIELD_ALIASES, {
  describeFor: (name) =>
    `Not a field. Few-shot examples go in "fewShots"; "${name}" is rejected with a 400.`,
  messageFor: (name) =>
    `Unsupported field "${name}". Attach few-shot examples via "fewShots": [{ "input": "...", "output": "..." }].`
});

/**
 * The dashboard's task category and its per-category sub-configs. Every task
 * created on v1 is GENERATION — the adapter writes the category as a constant
 * and reads none of these — so a create carrying `category: "CLASSIFICATION"`
 * answered 201 for a classification task that was never made (NEX-6503).
 *
 * CREATE ONLY, never on update: `category` is emitted by every task read
 * (`TaskSummarySchema`), and an emitted field is never refused on update
 * because GET-modify-PATCH would turn the unchanged value into a 400.
 */
const V1_CATEGORY_FIELDS = [
  "category",
  "classification",
  "extraction",
  "translation",
  "summarisation"
] as const;

const v1CategoryFields = refusedFields(V1_CATEGORY_FIELDS, {
  describeFor: (name) => {
    if (name === "category") {
      return 'Not a field. Every task created on v1 is GENERATION; "category" is rejected with a 400.';
    }
    return `Not a field. v1 creates GENERATION tasks only, configured under "generation"; "${name}" is rejected with a 400.`;
  },
  messageFor: (name) => {
    if (name === "category") {
      return 'Unsupported field "category". Every task created on v1 is GENERATION; the other categories are dashboard-only today.';
    }
    return `Unsupported field "${name}". v1 creates GENERATION tasks only; put the task configuration under "generation".`;
  }
});

// A task's kind is fixed at create, and no update body declares it: a PATCH
// carrying `kind` reported success for a change that never happened.
const UPDATE_FIXED_FIELDS = ["kind"] as const;

const fixedKindOnUpdate = refusedFields(UPDATE_FIXED_FIELDS, {
  describeFor: () =>
    'Not a field on update. A task\'s kind is fixed at create; "kind" is rejected with a 400.',
  messageFor: () =>
    'Unsupported field "kind". A task\'s kind is fixed at create; create a new task to get the other kind.'
});

export const CreateTaskBodySchema = z
  .object({
    name: z.string().trim().min(1, "Name is required"),
    // What answers the task, fixed for its lifetime: GENERATIVE runs on a model
    // that writes, DECISION on a decision model that answers typed questions.
    // No update body declares it.
    kind: AITaskKindSchema.default("GENERATIVE").describe(
      "What answers the task, fixed at create. GENERATIVE (default): a model that writes. DECISION: a decision model (such as jev-1.13.0) that answers typed questions — outputFormat json, and a jsonOutputSchema of boolean, string-enum and bounded-integer fields."
    ),
    description: z.string().optional(),
    modelName: z.string().min(1, "Model name is required"),
    // Same three values as before, sourced from the DB enum instead of being
    // re-spelled as string literals — so a provider added to the schema cannot
    // silently drift out of the public contract.
    modelProvider: z.nativeEnum(DbEnum.ModelProvider),
    // BYOM. `modelName` / `modelProvider` stay REQUIRED beside it and are not
    // redundant: they are what the task falls back to, and the executor
    // discards the whole stored config — `customModelId` with it — when either
    // is blank, so a config that names only a custom model would silently run
    // gpt-4o on the platform key.
    customModelId: CustomModelIdSchema.optional(),
    prompt: z.string().optional(),
    temperature: z.number().min(0).max(2).default(0.7),
    // The agent surface's own field, taken from the same canonical shape — so one
    // vocabulary on both, and a level the model does not offer, or one the
    // organization has not opened, is refused by the same judge. Undeclared, the
    // lenient object STRIPPED it: a 200 that stored no level.
    reasoningLevel: ModelConfigBaseShape.reasoningLevel,
    // The four names `reasoningLevel` replaced are REFUSED with a 400 naming it.
    // Undeclared, this lenient object would strip them the same way.
    ...RetiredEffortLevelFieldsShape,
    inputFormat: z.enum(["text", "json"]).default("text"),
    outputFormat: z.enum(["text", "json", "template"]).default("text"),
    // Backwards-compat: `multimodal` is canonically nested under `generation`, but
    // accept it at the top level too (early adopters passed it there) and fold it in.
    multimodal: z.boolean().optional(),
    // When false (default), creating a task whose prompt exactly matches an
    // existing task in the same organisation is rejected with DUPLICATE_TASK_PROMPT.
    // Set true to intentionally allow the duplicate.
    allowDuplicate: z.boolean().default(false),
    // Few-shot demonstrations, stored beside the prompt rather than inside it
    // and replayed as user/assistant message pairs at inference (NEX-2592).
    // Persisted in the order given.
    fewShots: z.array(TaskFewShotInputSchema).optional(),
    generation: z.object({
      multimodal: z.boolean().default(false),
      // Accepted here because callers naturally group the prompt with the rest
      // of the generation config; folded into the top-level prompt on persist.
      prompt: z.string().optional(),
      expectedInput: z.string().optional(),
      jsonInputSchema: JsonSchemaDocumentSchema.nullish(),
      expectedOutput: z.string().optional(),
      jsonOutputSchema: JsonSchemaDocumentSchema.nullish(),
      documentTemplateId: z.string().optional()
    }),
    ...promptAliases.fields,
    ...fewShotAliases.fields,
    ...v1CategoryFields.fields
  })
  .transform((data) => {
    if (data.multimodal === undefined) return data;
    return {
      ...data,
      generation: {
        ...data.generation,
        multimodal: data.generation.multimodal || data.multimodal
      }
    };
  })
  .superRefine((data, ctx) => {
    promptAliases.refine(data, ctx);
    fewShotAliases.refine(data, ctx);
    v1CategoryFields.refine(data, ctx);

    // Validate generation fields based on input/output format
    if (data.inputFormat === "text" && !data.generation.expectedInput) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["generation", "expectedInput"],
        message: 'expectedInput is required when inputFormat is "text"'
      });
    }

    if (data.inputFormat === "json" && !data.generation.jsonInputSchema) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["generation", "jsonInputSchema"],
        message: 'jsonInputSchema is required when inputFormat is "json"'
      });
    }

    if (data.outputFormat === "text" && !data.generation.expectedOutput) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["generation", "expectedOutput"],
        message: 'expectedOutput is required when outputFormat is "text"'
      });
    }

    if (data.outputFormat === "json" && !data.generation.jsonOutputSchema) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["generation", "jsonOutputSchema"],
        message: 'jsonOutputSchema is required when outputFormat is "json"'
      });
    }

    if (data.outputFormat === "template" && !data.generation.documentTemplateId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["generation", "documentTemplateId"],
        message: 'documentTemplateId is required when outputFormat is "template"'
      });
    }

    // Reject (rather than silently drop) a schema that would never be persisted:
    // jsonInputSchema/jsonOutputSchema are only stored when the matching format is
    // "json". A common mistake is nesting `outputFormat` under `generation` or at
    // the wrong root, leaving the format at its "text" default — without this the
    // call returns success and the schema is lost (NEX-2441). `!= null` excludes a
    // `null` (explicitly empty, nothing to lose) — only a real schema can be dropped.
    if (data.generation.jsonInputSchema != null && data.inputFormat !== "json") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["generation", "jsonInputSchema"],
        message: `jsonInputSchema provided but inputFormat is "${data.inputFormat}", not "json" — set inputFormat: "json" at the body root to keep the schema`
      });
    }

    if (data.generation.jsonOutputSchema != null && data.outputFormat !== "json") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["generation", "jsonOutputSchema"],
        message: `jsonOutputSchema provided but outputFormat is "${data.outputFormat}", not "json" — set outputFormat: "json" at the body root to keep the schema`
      });
    }
  });

export const UpdateTaskBodySchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    description: z.string().optional(),
    modelName: z.string().min(1).optional(),
    modelProvider: z.nativeEnum(DbEnum.ModelProvider).optional(),
    // Sending it attaches the custom model. CHANGING THE MODEL WITHOUT IT
    // DETACHES: a PATCH carrying `modelName` or `modelProvider` and no
    // `customModelId` clears the stored one, because otherwise "put this task
    // back on a platform model" would be unexpressible and the custom endpoint
    // would keep serving every run while the response reported the new name.
    customModelId: CustomModelIdSchema.optional(),
    prompt: z.string().optional(),
    temperature: z.number().min(0).max(2).optional(),
    // Omitted ⇒ the stored level is kept, unless `modelProvider` is sent: a
    // provider switch clears it, because the old provider's level would
    // otherwise be applied to the new one. Send it beside the switch to keep one.
    reasoningLevel: ModelConfigBaseShape.reasoningLevel,
    // The four names `reasoningLevel` replaced are REFUSED with a 400 naming it.
    ...RetiredEffortLevelFieldsShape,
    inputFormat: z.enum(["text", "json"]).optional(),
    outputFormat: z.enum(["text", "json", "template"]).optional(),
    // Backwards-compat: every `generation` sub-field is also accepted at the body
    // ROOT and folded into `generation` below. Pre-0.2.19 CLIs and direct API
    // callers placed these (notably `jsonOutputSchema`) at the top level; the
    // base z.object() silently strips unknown keys, so a PATCH like
    // `{ "jsonOutputSchema": {...} }` returned success:true while persisting
    // nothing — silent data loss (NEX-2439).
    multimodal: z.boolean().optional(),
    expectedInput: z.string().optional(),
    jsonInputSchema: JsonSchemaDocumentSchema.nullish(),
    expectedOutput: z.string().optional(),
    jsonOutputSchema: JsonSchemaDocumentSchema.nullish(),
    documentTemplateId: z.string().optional(),
    // REPLACES the stored set, it does not append: the array sent is the array
    // the task ends up with, and `[]` removes every example. Omitting the key
    // leaves them untouched, like every other field on this PATCH.
    //
    // Whole-set replacement rather than per-example ids because the ids are not
    // something a caller of this route ever holds — `task get` is the only read
    // that carries them, and an append-only field would leave "remove the third
    // example" unexpressible here.
    fewShots: z.array(TaskFewShotInputSchema).optional(),
    generation: z
      .object({
        multimodal: z.boolean().optional(),
        // Accepted here because callers naturally group the prompt with the rest
        // of the generation config; folded into the top-level prompt on persist.
        prompt: z.string().optional(),
        expectedInput: z.string().optional(),
        jsonInputSchema: JsonSchemaDocumentSchema.nullish(),
        expectedOutput: z.string().optional(),
        jsonOutputSchema: JsonSchemaDocumentSchema.nullish(),
        documentTemplateId: z.string().optional()
      })
      .optional(),
    ...fixedKindOnUpdate.fields,
    ...promptAliases.fields,
    ...fewShotAliases.fields
  })
  .transform((data) => {
    const {
      multimodal,
      expectedInput,
      jsonInputSchema,
      expectedOutput,
      jsonOutputSchema,
      documentTemplateId,
      generation,
      ...rest
    } = data;

    // Fold any generation sub-field passed at the body ROOT into `generation`.
    // A nested `generation.<field>` always takes precedence over the top-level
    // mirror, so a caller mixing both gets the canonical (nested) value.
    const folded: {
      multimodal?: boolean;
      prompt?: string;
      expectedInput?: string;
      jsonInputSchema?: unknown;
      expectedOutput?: string;
      jsonOutputSchema?: unknown;
      documentTemplateId?: string;
    } = {
      ...(multimodal !== undefined ? { multimodal } : {}),
      ...(expectedInput !== undefined ? { expectedInput } : {}),
      ...(jsonInputSchema !== undefined ? { jsonInputSchema } : {}),
      ...(expectedOutput !== undefined ? { expectedOutput } : {}),
      ...(jsonOutputSchema !== undefined ? { jsonOutputSchema } : {}),
      ...(documentTemplateId !== undefined ? { documentTemplateId } : {}),
      ...(generation ?? {})
    };

    // Match CreateTaskBodySchema: a nested `generation.multimodal: true` wins
    // over a top-level `multimodal: false` (logical OR), so the same payload
    // resolves consistently across create and update.
    if (multimodal !== undefined) {
      folded.multimodal = (generation?.multimodal ?? false) || multimodal;
    }

    // Keep a single, stable output shape (always a `generation` key) so callers
    // get a consistent type; a scalar-only PATCH leaves it undefined, which the
    // adapter treats as "don't touch the existing generation config".
    return {
      ...rest,
      generation: Object.keys(folded).length === 0 ? undefined : folded
    };
  })
  .superRefine((data, ctx) => {
    fixedKindOnUpdate.refine(data, ctx);
    promptAliases.refine(data, ctx);
    fewShotAliases.refine(data, ctx);
  });

// ============================================================================
// EXECUTION BODY & RESPONSE SCHEMAS
// ============================================================================

export const GenerateDocumentTemplateBodySchema = z.object({
  variables: z.record(z.string(), z.unknown())
});

export const GenerateDocumentTemplateResponseSchema = z.object({
  url: z.string()
});

export const ExecuteTaskBodySchema = z.object({
  input: z.union([z.string(), z.record(z.string(), z.unknown())]),
  /**
   * Run THIS call on another model, leaving the task's stored binding alone.
   *
   * A task binds a prompt to a model, and that binding is right for the prompt
   * and wrong for the call: the same assessor runs over every notice in a
   * nightly sweep, where cheap and good-enough wins, and over the one record a
   * human is about to bid on, where the frontier model is worth 15x. Without
   * this the only way to say that was a second task — which means a second copy
   * of the prompt, and the prompt is the artifact worth keeping single
   * (NEX-2962).
   *
   * Nothing is persisted: the task, its versions and every other caller are
   * unaffected by an override. Absent means the task's own model, so an existing
   * body behaves exactly as it did.
   */
  modelOverride: AiTaskModelOverrideSchema.optional()
});

export const ExecuteTaskResponseSchema = z.object({
  output: z.unknown(),
  outputType: z.string(),
  /** Present only when the run fell back from a model that is not live. */
  modelNotice: AiTaskModelNoticeSchema.optional()
});

export const DeleteTaskResponseSchema = z.object({
  id: z.string(),
  deleted: z.literal(true)
});

/**
 * Payload of `DELETE /skills/document-templates/:templateId`.
 *
 * Shape-identical to {@link DeleteTaskResponseSchema} and deliberately its own
 * schema rather than a shared one: the task schema names the TASK route, and a
 * single schema serving two routes makes a later divergence a silent change to
 * whichever route was not being edited.
 */
export const DeleteDocumentTemplateResponseSchema = z.object({
  id: z.string(),
  deleted: z.literal(true)
});

/**
 * Body for `POST /skills/tasks/:taskId/duplicate` — every field optional, so an
 * empty body is a plain copy.
 *
 * The 409 on `POST /skills/tasks` has recommended duplicating a task since it was
 * written, and until now no route, CLI command or SDK method by that name existed
 * on this surface — the error named the one path a caller could not take
 * (NEX-2962). The internal dashboard has had `POST /ai-tasks/:taskId/duplicate`
 * the whole time; this is the same operation, org-scoped, with the model knob the
 * conflict is usually about.
 *
 * 🚨 THE POINT OF THE MODEL FIELDS IS THAT THE REST OF THE CONFIG IS **COPIED**,
 * NOT RE-SENT. Re-creating a variant through `POST /skills/tasks` means resending
 * every field, and any field the caller forgets is silently taken from that
 * route's DEFAULTS rather than from the task being copied. Measured on the
 * engagement that filed this ticket: a 94,268-char prompt forked to a cheaper
 * model landed on `temperature: 0.7` against the original's `0.2`, because
 * `CreateTaskBodySchema` defaults an unsent temperature and nothing reported the
 * change. A duplicate carries the source task's config forward and changes only
 * what this body names.
 */
export const DuplicateTaskBodySchema = z
  // `strictObject`, unlike its three sibling task bodies, which are ledgered as
  // lenient. Every field here is optional, so a stripped typo is not a 400 on
  // something else later — `{ modelname: "claude-haiku-4-5" }` would be a
  // successful PLAIN COPY reported as the model fork the caller asked for, and
  // the response would name the source's model with nothing to say why.
  .strictObject({
    /** Defaults to the source task's name suffixed with " (Copy)". */
    name: z.string().trim().min(1).optional(),
    description: z.string().optional(),
    /**
     * Re-point the copy at another model. Both halves are required together — a
     * lone `modelName` beside the source's provider is how a task ends up
     * addressing an Anthropic endpoint with an OpenAI model id.
     *
     * `customModelId` is NOT carried over from the source when these are set: a
     * BYOM endpoint replaces the routing outright, so inheriting one would run
     * the customer's endpoint and ignore the model just named. Name one here
     * instead.
     *
     * Naming a model rebuilds the routing through `mergeModelConfig`, the same
     * function `PATCH /skills/tasks/:id` uses: `temperature` and the rest of the
     * non-routing config carry over from the source, and the tuning chosen for the
     * source's model (`reasoningLevel`, `thinkingDisplay`) is cleared. Omit these fields and
     * the copy keeps the source's model configuration whole.
     */
    modelName: z.string().min(1).optional(),
    modelProvider: z.nativeEnum(DbEnum.ModelProvider).optional(),
    customModelId: CustomModelIdSchema.optional()
  })
  .superRefine((data, ctx) => {
    // Half a pair is refused rather than half-applied. `mergeModelConfig` takes
    // both names as REQUIRED because it rebuilds the routing from them, so a
    // body carrying one of them could only ever be completed from the source
    // task — which is the silent-inheritance shape this whole route exists to
    // remove. `POST /skills/tasks` requires both for the same reason.
    const namesAModel =
      data.modelName !== undefined ||
      data.modelProvider !== undefined ||
      data.customModelId !== undefined;
    if (!namesAModel) return;

    for (const field of ["modelName", "modelProvider"] as const) {
      if (data[field] === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `${field} is required when re-pointing a duplicate at another model — send modelName and modelProvider together. Omit all model fields to copy the source task's model.`
        });
      }
    }
  });

/**
 * The public-v1 twin of `CollectionNameSchema` / `CollectionDisplayNameSchema`
 * in `api/domains/collections/zcollections.ts`. Two contract layers reach one
 * table, so they have to refuse the same values — the CLI drives this pair and
 * the dashboard drives the other, and a guard on one door is not a guard.
 *
 * `.trim()` before `.min(1)`: reversed, the length is measured on the untrimmed
 * value and `"   "` passes. The `.max()` matches the internal layer, which had
 * one here all along — this pair had none, so an unbounded string reached a
 * `@unique` column.
 */
const V1CollectionNameSchema = z.string().trim().min(1, "Name is required").max(255);

const V1CollectionDisplayNameSchema = z
  .string()
  .trim()
  .min(1, "displayName must not be blank")
  .max(255);

export const CreateCollectionBodySchema = z.object({
  name: V1CollectionNameSchema,
  displayName: V1CollectionDisplayNameSchema.optional(),
  description: z.string().optional(),
  k: z.number().int().min(1).default(10),
  reranker: RerankerModelSchema.optional(),
  preciseResponses: z.boolean().default(false),
  includeMetadata: z.boolean().default(false)
});

// ============================================================================
// EXTERNAL TOOL (CUSTOM_MANIFEST)
// ============================================================================

export const ExternalToolIdParamSchema = z.object({
  // Enforce UUID at the seam so the adapter's `${id}::uuid` cast never
  // sees a malformed value — bad input fails with a clean 400 instead
  // of leaking a Postgres invalid-syntax error as a 500.
  externalToolId: z.string().uuid()
});

export const ExternalToolAuthSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("none") }),
  z.object({
    type: z.literal("service_http"),
    apiKey: z.string().min(1, "apiKey is required for service_http"),
    authorization_type: z.enum(["basic", "bearer", "custom"]),
    custom_header_name: z.string().optional(),
    custom_header_prefix: z.string().optional()
  }),
  z.object({
    type: z.literal("user_http"),
    authorization_type: z.enum(["basic", "bearer", "custom"]),
    custom_header_name: z.string().optional(),
    custom_header_prefix: z.string().optional()
  }),
  z.object({
    type: z.literal("oauth"),
    grant_type: z.string(),
    client_id: z.string(),
    client_secret: z.string(),
    // Both are token endpoints this backend POSTs `client_secret` and a
    // decrypted `refresh_token` to (NEX-3519 item 6).
    client_url: optionalSafeExternalUrlSchema,
    authorization_url: optionalSafeExternalUrlSchema,
    scope: z.string().optional(),
    // One list, owned by the manifest schema: a value the token exchange cannot
    // perform is refused here instead of failing after the user has already been
    // sent to the provider and back.
    tokenExchangeMethod: TokenExchangeMethodSchema.optional()
  }),
  z.object({
    type: z.literal("user_oauth"),
    grant_type: z.string(),
    authorization_type: z.enum(["basic", "bearer", "custom"]).optional(),
    custom_header_name: z.string().optional(),
    custom_header_prefix: z.string().optional()
  }),
  z.object({ type: z.literal("keys") })
]);

// Custom HTTP headers (header-name → value) applied to every action of the
// tool, independent of auth — primarily a browser-like User-Agent to bypass
// anti-bot WAF 403s (Workday/Cloudflare/Akamai). See NEX-2017.
export const ExternalToolCustomHeadersSchema = z.record(z.string().min(1), z.string());

export const CreateExternalToolBodySchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  description: z.string().optional(),
  imageUrl: z.string().url("imageUrl must be a valid URL").optional(),
  documentation: z.string().optional(),
  openApiSpec: z.string().min(1, "OpenAPI spec is required"),
  // TENANT-WRITTEN, and the backend later dials it with the tool's DECRYPTED
  // credentials attached. Same guard as `CustomModel.baseUrl` — see
  // `safe-external-url.schema.ts` (NEX-3519 item 5).
  endpointUrl: safeExternalUrlSchema,
  auth: ExternalToolAuthSchema,
  customHeaders: ExternalToolCustomHeadersSchema.optional()
});

export const UpdateExternalToolBodySchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().nullable().optional(),
  documentation: z.string().nullable().optional(),
  openApiSpec: z.string().min(1).optional(),
  endpointUrl: optionalSafeExternalUrlSchema.optional(),
  auth: ExternalToolAuthSchema.optional(),
  // Pass `{}` to clear all custom headers, or omit to leave unchanged.
  customHeaders: ExternalToolCustomHeadersSchema.optional()
});

export const DeleteExternalToolParamsSchema = z.object({
  // Strict literal "true" or omitted. Any other value (e.g. "false", "1") is
  // rejected with 400 — the v1 controller parses against this schema and
  // throws BadRequestException on failure. `z.coerce.boolean()` would wrongly
  // treat "false" as truthy, hence the literal.
  force: z.literal("true").optional()
});

// Query params for PATCH /skills/external-tools/:id. `force=true` skips the
// breaking-change guard so a spec refresh may drop/rename an action key that
// downstream wiring (workflow plugin nodes, agent tool configs) still binds.
// Same strict-literal rule as the delete guard above.
export const UpdateExternalToolParamsSchema = z.object({
  force: z.literal("true").optional()
});

export const DeleteExternalToolResponseSchema = z.object({
  deleted: z.literal(true)
});

// Shape of the structured `details` returned by DELETE /skills/external-tools/:id
// when the tool has attached AgentToolConfigs (HTTP 409, code TOOL_HAS_ATTACHMENTS).
//
// This schema is the SINGLE definition of the shape. The producer —
// `ToolHasAttachmentsErrorDetails` in apps/backend's external-tool.errors.ts —
// is a `z.infer` alias of it, so a field added here changes what is actually
// sent. The CLI cannot safeParse (importing Zod is the +5MB its hand-declared
// wire types exist to avoid); its copy in packages/cli/src/external-tool-wire-types.ts
// is compared against this schema by external-tool-wire-types.conformance.ts,
// so a change here reaches it as a typecheck failure.
export const ToolHasAttachmentsDetailsSchema = z.object({
  total: z.number().int().nonnegative(),
  sample: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      agentId: z.string(),
      agentName: z.string()
    })
  )
});

/**
 * The inferred shape, exported so consumers that cannot import Zod can still
 * name it. `z.infer` inside a package with no `zod` dependency resolves to
 * `any`, and an `any` on one side of a structural comparison satisfies every
 * assertion — so a gate written that way passes while comparing nothing. The
 * CLI is exactly that consumer.
 */
export type TToolHasAttachmentsDetails = z.infer<typeof ToolHasAttachmentsDetailsSchema>;

// Shape of the structured `details` returned by PATCH /skills/external-tools/:id
// when refreshing `openApiSpec` would drop/rename an action key still bound by
// downstream wiring (HTTP 409, code TOOL_SPEC_BREAKING_CHANGE). `removedActions`
// lists the dropped operationIds that are still bound downstream (the breaking
// subset, so its count lines up with the bindings); `bindings` samples the
// workflow nodes / agent tool configs that reference them, and `total` is the
// full count of such bindings.
//
// Same single-definition arrangement as ToolHasAttachmentsDetailsSchema above:
// the backend's ToolSpecBreakingChangeErrorDetails infers from this, and the
// CLI's copy is gated against it.
export const ToolSpecBreakingChangeDetailsSchema = z.object({
  removedActions: z.array(z.string()),
  total: z.number().int().nonnegative(),
  bindings: z.array(
    z.object({
      kind: z.enum(["workflow", "agent"]),
      id: z.string(),
      label: z.string(),
      action: z.string()
    })
  )
});

/** See {@link TToolHasAttachmentsDetails} for why the inferred type is exported. */
export type TToolSpecBreakingChangeDetails = z.infer<typeof ToolSpecBreakingChangeDetailsSchema>;

export const ExternalToolDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  documentation: z.string().nullable(),
  type: z.literal("CUSTOM_MANIFEST"),
  endpointUrl: z.string().nullable(),
  status: z.string(),
  actionsCount: z.number(),
  authType: z.string(),
  createdAt: z.string()
});

export const ListExternalToolsResponseSchema = z.object({
  items: z.array(ExternalToolDetailSchema),
  total: z.number()
});

// ============================================================================
// TEST EXTERNAL TOOL
// ============================================================================

export const TestExternalToolBodySchema = z.object({
  operationId: z.string().min(1, "operationId is required"),
  input: z.record(z.string(), z.unknown()),
  // `ToolCredentials.id` is `@db.Uuid`, matching the uuid-checked `toolCredentialId`
  // on `AgentToolConfigSchema`.
  toolCredentialId: z.string().uuid().optional()
});

export const TestExternalToolResponseSchema = z.object({
  status: z.enum(["success", "error"]),
  output: z.unknown(),
  error: z.string().optional(),
  executionTimeMs: z.number()
});

// ============================================================================
// COLLECTION DOCUMENTS
// ============================================================================

// A schema for one collection document is not declared here: the list route
// replies `{ data, meta }`, so an element schema alone cannot describe its
// payload, and an unreferenced one reads as a contract while guaranteeing
// nothing. Declare it together with the wrapper when that route gets a Response.

export const ListCollectionDocumentsParamsSchema = PaginationParamsSchema.extend({});

// ============================================================================
// COLLECTION STATISTICS
// ============================================================================

export const CollectionStatisticsSchema = z.object({
  documentCount: z.number(),
  totalSizeBytes: z.number(),
  embeddedCount: z.number(),
  pendingCount: z.number(),
  lastUpdatedAt: z.coerce.date().nullable()
});

// ============================================================================
// COLLECTION SEARCH
// ============================================================================

export const SearchCollectionBodySchema = z.object({
  query: z.string().trim().min(1),
  limit: z.coerce.number().int().min(1).max(50).default(10),
  includeMetadata: z.boolean().default(false)
});

export const SearchMultipleCollectionsBodySchema = z.object({
  query: z.string().trim().min(1),
  collectionIds: z.array(z.string().uuid()).min(1),
  limit: z.coerce.number().int().min(1).max(50).default(10)
});

export const SearchResultSchema = z.object({
  results: z.array(
    z.object({
      text: z.string(),
      score: z.number(),
      documentId: z.string(),
      documentName: z.string(),
      metadata: z.unknown().nullable()
    })
  )
});

// ----------------------------------------------------------------------------
// COLLECTION CONTENT QUERY (semantic / ZeroEntropy retrieval)
// ----------------------------------------------------------------------------
// Unlike `search` (which matches document names), `query` runs the real content
// retrieval used by agents at runtime and returns matching content snippets.

/**
 * The most results one content query answers with, whether the caller names a
 * `limit` or the collection's own `k` stands in for it. A collection's `k` has
 * no maximum of its own, and the retrieval store refuses a request above its
 * own ceiling.
 */
export const QUERY_COLLECTION_MAX_LIMIT = 50;

export const QueryCollectionBodySchema = z.object({
  query: z.string().trim().min(1),
  /** Omitted → the collection's own `k`, up to {@link QUERY_COLLECTION_MAX_LIMIT}. */
  limit: z.coerce.number().int().min(1).max(QUERY_COLLECTION_MAX_LIMIT).optional(),
  /** Omitted → the collection's own `includeMetadata` decides. */
  includeMetadata: z.boolean().optional(),
  /** Narrows retrieval to documents matching these attributes (e.g. `{ language: "fr" }`). */
  metadataFilter: DocumentMetadataFilterSchema.optional()
});

export const QueryResultSchema = z.object({
  results: z.array(
    z.object({
      content: z.string(),
      score: z.number(),
      documentId: z.string().optional(),
      metadata: z.unknown().nullable()
    })
  )
});

// ============================================================================
// COLLECTION UPDATE / DELETE
// ============================================================================

export const UpdateCollectionBodySchema = z.object({
  displayName: V1CollectionDisplayNameSchema.optional(),
  description: z.string().optional(),
  k: z.number().int().min(1).optional(),
  /** Same contract as CREATE — see {@link RerankerModelSchema}. */
  reranker: RerankerModelSchema.optional(),
  preciseResponses: z.boolean().optional(),
  includeMetadata: z.boolean().optional(),
  /**
   * Whether the collection is retrievable. `false` takes it out of knowledge
   * retrieval — `ChatAssistantKnowledgeService` skips an inactive collection
   * and the collection search filters on `isActive: true` — without deleting
   * it or its documents.
   *
   * 🔴 THIS FIELD IS WIRED RATHER THAN REFUSED, WHICH IS THE OPPOSITE
   * DISPOSITION TO THE ALIAS FIELDS ABOVE, AND THE DISCRIMINATOR IS WHETHER
   * THIS SURFACE EMITS THE NAME. `promptText` and `examples` are refused
   * because no v1 response carries either, so no caller can be round-tripping
   * one — the 400 only ever reaches someone who guessed the field name.
   * `isActive` is emitted by `CollectionSummarySchema`, so it is on every
   * collection a caller reads back, and refusing it would fail the ordinary
   * read-modify-write: GET a collection, change `displayName`, PATCH the
   * object back, and the unchanged `isActive` that came out of the GET turns a
   * working call into a 400.
   *
   * Omitting it silently was the third option and the only wrong one. A plain
   * `z.object()` strips what it does not declare, so the field left this
   * schema with a 200 and reached no layer below — the surface reported a
   * deactivation it had not performed, and the caller's own next GET was the
   * only place that disagreed.
   */
  isActive: z.boolean().optional()
});

// `Document.id` is `@db.Uuid`, exactly like the `collectionId` beside it.
export const CollectionIdDocumentIdParamSchema = z.object({
  collectionId: z.string().uuid(),
  documentId: z.string().uuid()
});
